import { useEffect, useMemo, useRef, useState } from 'react';
import { u3147EnrichSessionTask, u3147SessionTaskNeedsEnrich, type U3147TimeSession } from './U3147Service';
import { useU3147FindTasksForProjectHandlers } from './U3147UseFindProjectSubprojectHandlers';

function u3147SessionTasksFetchKey(session: U3147TimeSession): string | null {
  const projectId = session.projectId?.trim() || session.project?.id?.trim();
  if (!projectId) return null;
  const subprojectId = session.subprojectId?.trim() || session.subproject?.id?.trim() || '';
  return `${projectId}|${subprojectId}`;
}

/** Complète les libellés tâche des sessions via findTasksForProject (réponse day souvent minimale). */
export function useU3147EnrichSessionsTasks(sessions: U3147TimeSession[]): U3147TimeSession[] {
  const { fetchTasks } = useU3147FindTasksForProjectHandlers();
  const fetchTasksRef = useRef(fetchTasks);
  fetchTasksRef.current = fetchTasks;
  const [enrichedById, setEnrichedById] = useState<Record<string, U3147TimeSession>>({});

  const sessionsKey = useMemo(
    () =>
      sessions
        .map((session) => `${session.id}:${session.version}:${session.taskId ?? ''}:${session.task?.id ?? ''}`)
        .join('\n'),
    [sessions],
  );

  useEffect(() => {
    setEnrichedById({});
  }, [sessionsKey]);

  useEffect(() => {
    const targets = sessions.filter((session) => u3147SessionTaskNeedsEnrich(session));
    if (targets.length === 0) return undefined;

    let cancelled = false;

    void (async () => {
      const fetchKeys = new Map<string, { projectId: string; subprojectId: string | null }>();
      for (const session of targets) {
        const key = u3147SessionTasksFetchKey(session);
        if (!key) continue;
        const [projectId, subprojectId] = key.split('|');
        fetchKeys.set(key, { projectId, subprojectId: subprojectId || null });
      }

      const taskLists = new Map<string, Awaited<ReturnType<typeof fetchTasks>>>();
      await Promise.all(
        [...fetchKeys.entries()].map(async ([key, body]) => {
          const list = await fetchTasksRef.current(body);
          taskLists.set(key, list);
        }),
      );

      if (cancelled) return;

      const next: Record<string, U3147TimeSession> = {};
      for (const session of targets) {
        const key = u3147SessionTasksFetchKey(session);
        if (!key) continue;
        const enriched = u3147EnrichSessionTask(session, taskLists.get(key) ?? []);
        if (enriched !== session) {
          next[session.id] = enriched;
        }
      }

      if (Object.keys(next).length > 0) {
        setEnrichedById((prev) => ({ ...prev, ...next }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessions, sessionsKey]);

  return useMemo(
    () => sessions.map((session) => enrichedById[session.id] ?? session),
    [sessions, enrichedById],
  );
}
